// src/core/scope/useOperationalScope.js

import { useMemo } from "react";

import {
  PERMISSIONS,
  PLATFORM_ROLES,
  normalizeId,
  normalizePlatformRole,
  getAccessProfileState,
  getUserRole,
  getTenantId,
  getFacilityIds,
  getPrimaryFacilityId,
  hasPermission,
  isOwnerAdmin as canonicalIsOwnerAdmin,
  isFinanceAdmin as canonicalIsFinanceAdmin,
  isOperationsAdmin as canonicalIsOperationsAdmin,
  isAgencyAdmin as canonicalIsAgencyAdmin,
  isPlatformAdmin as canonicalIsPlatformAdmin,
  canAccessTenant,
  canAccessFacility,
  isFacilityPlanner,
  isFacilityClient,
  isWorker,
} from "../../security/permissions";

/*
 * ShiftBridge Canonical Operational Scope
 *
 * AUTHENTICATED ACTOR != OPERATIONAL WORKSPACE
 *
 * The signed-in account keeps its real role and permissions.
 * Admin users may open a facility-scoped workspace (Planner, Pulse,
 * worker preview, etc.) without pretending to be another Firebase user.
 */

export const OPERATIONAL_SCOPE_STATUS =
  Object.freeze({
    READY: "ready",
    MISSING_IDENTITY: "missing_identity",
    MISSING_ROLE: "missing_role",
    ACCESS_DISABLED: "access_disabled",
    INVALID_WORKSPACE: "invalid_workspace",
    MISSING_TENANT: "missing_tenant",
    MISSING_FACILITY: "missing_facility",
  });

export function normalizeOperationalId(value) {
  return normalizeId(value);
}


function getRequestedFacilityId(profile = {}, options = {}) {
  return normalizeId(
    options?.facilityId ||
      options?.viewAsFacilityId ||
      profile?.viewAsFacilityId ||
      profile?.selectedFacilityId ||
      ""
  );
}

function getRequestedTenantId(profile = {}, options = {}) {
  return normalizeId(
    options?.tenantId ||
      options?.viewAsTenantId ||
      getTenantId(profile) ||
      ""
  );
}

function getRequestedWorkspaceRole(options = {}) {
  /*
   * Workspace identity must be explicit.
   *
   * Generic values such as options.mode may describe
   * shift behavior ("pool", "manual", etc.) and must
   * never become authorization roles.
   */
  return normalizePlatformRole(
    options?.workspaceRole ||
      options?.workspace ||
      ""
  );
}

export default function useOperationalScope(profile, options = {}) {
  return useMemo(() => {
    const safeProfile =
      profile && typeof profile === "object" ? profile : {};

    const uid = String(
      safeProfile?.uid || safeProfile?.id || ""
    ).trim();

    const email = String(safeProfile?.email || "")
      .trim()
      .toLowerCase();

    const actorRole = getUserRole(safeProfile);

    const {
      accessProfile,
      hasAccessProfile,
      isAccessEnabled,
    } = getAccessProfileState(safeProfile);

const actorTenantId =
  getTenantId(safeProfile);

const requestedTenantId =
  getRequestedTenantId(
    safeProfile,
    options
  );

  const requestedTenantAllowed =
  Boolean(requestedTenantId) &&
  canAccessTenant(
    safeProfile,
    requestedTenantId
  );

/*
 * An explicitly resolved tenant that the actor
 * cannot access is an invalid workspace request.
 *
 * Do not silently convert it into the actor's
 * normal tenant.
 */
const invalidRequestedTenant =
  Boolean(requestedTenantId) &&
  !requestedTenantAllowed;

const isOwnerAdmin =
  canonicalIsOwnerAdmin(
    safeProfile
  );

const isFinanceAdmin =
  canonicalIsFinanceAdmin(
    safeProfile
  );

const isOperationsAdmin =
  canonicalIsOperationsAdmin(
    safeProfile
  );

const isAgencyAdmin =
  canonicalIsAgencyAdmin(
    safeProfile
  );

const isPlatformAdmin =
  canonicalIsPlatformAdmin(
    safeProfile
  );

  /*
 * A workspace may request a tenant, but the request
 * does not become operational scope until the
 * authenticated actor is authorized for it.
 */
const tenantId =
  invalidRequestedTenant
    ? ""
    : requestedTenantAllowed
    ? requestedTenantId
    : actorTenantId;

 const permittedFacilityIds =
  getFacilityIds(safeProfile);

const canonicalPrimaryFacilityId =
  getPrimaryFacilityId(safeProfile);

const requestedFacilityId =
  getRequestedFacilityId(
    safeProfile,
    options
  );

const canViewAllFacilities =
  hasPermission(
    safeProfile,
    PERMISSIONS.CAN_VIEW_ALL_FACILITIES
  );

/*
 * A facility request is valid only inside a valid
 * tenant workspace.
 *
 * This prevents an invalid requested tenant from
 * being discarded while its requested facility is
 * still accepted under the actor's normal tenant.
 */

const requestedFacilityAllowed =
  Boolean(requestedFacilityId) &&
  requestedTenantAllowed &&
  canAccessFacility(
    safeProfile,
    requestedFacilityId,
    tenantId
  );

/*
 * A facility was requested but cannot be used in
 * the resolved tenant workspace.
 *
 * Treat that as an invalid workspace instead of
 * silently substituting another facility.
 */
const invalidRequestedFacility =
  Boolean(requestedFacilityId) &&
  !requestedFacilityAllowed;

/*
 * The actor's stored primary/default facilities are
 * only safe fallbacks while operating inside the
 * actor's own canonical tenant.
 *
 * When an Admin opens another tenant, a facility
 * must be explicitly selected for that tenant.
 */
const isActorTenantWorkspace =
  Boolean(tenantId) &&
  Boolean(actorTenantId) &&
  tenantId === actorTenantId;

const fallbackFacilityId =
  isActorTenantWorkspace
    ? (
        canonicalPrimaryFacilityId ||
        permittedFacilityIds[0] ||
        ""
      )
    : "";

const facilityId =
  invalidRequestedFacility
    ? ""
    : requestedFacilityAllowed
    ? requestedFacilityId
    : fallbackFacilityId;

const primaryFacilityId =
  canonicalPrimaryFacilityId;

/*
 * allowedFacilityIds describes the facilities that
 * are usable inside the resolved workspace.
 *
 * For a cross-tenant Admin workspace we must not
 * leak the actor's home-tenant facility list.
 */
const allowedFacilityIds =
  requestedFacilityAllowed &&
  canViewAllFacilities
    ? [requestedFacilityId]
    : isActorTenantWorkspace
    ? permittedFacilityIds
    : [];

const requestedWorkspaceRole =
  getRequestedWorkspaceRole(options);

const actorIsPlanner =
  isFacilityPlanner(safeProfile);

const actorIsFacilityPulseUser =
  isFacilityClient(safeProfile);

const actorIsWorker =
  isWorker(safeProfile);

/*
 * Operations authority is intentionally narrower
 * than general visibility.
 *
 * For example, Finance Admin may view Facility Pulse
 * but does not automatically operate the Planner.
 */
const canOperatePlannerWorkspace =
  actorIsPlanner ||
  isOwnerAdmin ||
  isOperationsAdmin ||
  isAgencyAdmin;

const canOperateFacilityPulseWorkspace =
  actorIsFacilityPulseUser ||
  isOwnerAdmin ||
  isOperationsAdmin ||
  isAgencyAdmin ||
  isFinanceAdmin;

const canPreviewWorkerWorkspace =
  actorIsWorker ||
  isOwnerAdmin ||
  isOperationsAdmin ||
  isAgencyAdmin;

/*
 * =========================================================
 * WORKSPACE ROLE AUTHORIZATION
 * =========================================================
 *
 * An account may operate:
 *
 * 1. its own canonical role;
 * 2. Planner only when Planner authority exists;
 * 3. Facility Pulse only when Pulse authority exists;
 * 4. Worker workspace only when worker/preview authority
 *    exists.
 *
 * A workspace request never changes the authenticated
 * actor's actual role.
 */
const requestedWorkspaceRoleAllowed =
  !requestedWorkspaceRole ||
  requestedWorkspaceRole === actorRole ||
  (
    requestedWorkspaceRole ===
      PLATFORM_ROLES.FACILITY_PLANNER &&
    canOperatePlannerWorkspace
  ) ||
  (
    [
      PLATFORM_ROLES.FACILITY_CLIENT,
      PLATFORM_ROLES.FACILITY_ADMIN_VIEWER,
    ].includes(requestedWorkspaceRole) &&
    canOperateFacilityPulseWorkspace
  ) ||
  (
    requestedWorkspaceRole ===
      PLATFORM_ROLES.WORKER &&
    canPreviewWorkerWorkspace
  );

const invalidRequestedWorkspaceRole =
  Boolean(requestedWorkspaceRole) &&
  !requestedWorkspaceRoleAllowed;

const workspaceRole =
  invalidRequestedWorkspaceRole
    ? ""
    : requestedWorkspaceRole ||
      actorRole;

const isPlannerWorkspace =
  workspaceRole ===
  PLATFORM_ROLES.FACILITY_PLANNER;

const isFacilityPulseWorkspace =
  [
    PLATFORM_ROLES.FACILITY_CLIENT,
    PLATFORM_ROLES.FACILITY_ADMIN_VIEWER,
  ].includes(workspaceRole);

const isWorkerWorkspace =
  workspaceRole ===
  PLATFORM_ROLES.WORKER;

/*
 * =========================================================
 * WORKSPACE HEALTH CONTRACT
 * =========================================================
 *
 * Every operational module should consume this contract
 * before starting Firestore listeners.
 *
 * Planner, Facility Pulse and Worker workspaces require
 * both tenant and facility scope.
 *
 * Enterprise/Admin workspaces may legitimately operate
 * at tenant level without selecting one facility.
 */

const hasInvalidWorkspaceRequest =
  invalidRequestedTenant ||
  invalidRequestedFacility ||
  invalidRequestedWorkspaceRole;

const hasTenantScope =
  Boolean(tenantId);

const hasFacilityScope =
  Boolean(facilityId);

const hasActorRole =
  Boolean(actorRole);

const workspaceRequiresFacilityScope =
  isPlannerWorkspace ||
  isFacilityPulseWorkspace ||
  isWorkerWorkspace;

const hasRequiredFacilityScope =
  !workspaceRequiresFacilityScope ||
  hasFacilityScope;


/*
 * Resolve one deterministic health status.
 *
 * Order matters. Earlier structural failures should
 * be surfaced before downstream scope failures.
 */
const scopeStatus =
  !uid
    ? OPERATIONAL_SCOPE_STATUS.MISSING_IDENTITY
    : !hasActorRole
    ? OPERATIONAL_SCOPE_STATUS.MISSING_ROLE
    : !isAccessEnabled
    ? OPERATIONAL_SCOPE_STATUS.ACCESS_DISABLED
    : hasInvalidWorkspaceRequest
    ? OPERATIONAL_SCOPE_STATUS.INVALID_WORKSPACE
    : !hasTenantScope
    ? OPERATIONAL_SCOPE_STATUS.MISSING_TENANT
    : !hasRequiredFacilityScope
    ? OPERATIONAL_SCOPE_STATUS.MISSING_FACILITY
    : OPERATIONAL_SCOPE_STATUS.READY;


const scopeErrorCode =
  invalidRequestedTenant
    ? "INVALID_TENANT_SCOPE"
    : invalidRequestedFacility
    ? "INVALID_FACILITY_SCOPE"
    : invalidRequestedWorkspaceRole
    ? "INVALID_WORKSPACE_ROLE"
    : scopeStatus ===
        OPERATIONAL_SCOPE_STATUS.MISSING_IDENTITY
    ? "MISSING_IDENTITY"
    : scopeStatus ===
        OPERATIONAL_SCOPE_STATUS.MISSING_ROLE
    ? "MISSING_ROLE"
    : scopeStatus ===
        OPERATIONAL_SCOPE_STATUS.ACCESS_DISABLED
    ? "ACCESS_DISABLED"
    : scopeStatus ===
        OPERATIONAL_SCOPE_STATUS.MISSING_TENANT
    ? "MISSING_TENANT_SCOPE"
    : scopeStatus ===
        OPERATIONAL_SCOPE_STATUS.MISSING_FACILITY
    ? "MISSING_FACILITY_SCOPE"
    : "";


const scopeErrorMessage =
  invalidRequestedTenant
    ? "The requested tenant is outside this account's authorized operational scope."
    : invalidRequestedFacility
    ? "The requested facility is outside this account's authorized operational scope."
    : invalidRequestedWorkspaceRole
    ? "This account is not authorized to operate the requested workspace."
    : scopeStatus ===
        OPERATIONAL_SCOPE_STATUS.MISSING_IDENTITY
    ? "Authenticated user identity is unavailable."
    : scopeStatus ===
        OPERATIONAL_SCOPE_STATUS.MISSING_ROLE
    ? "This account does not have a valid operational role."
    : scopeStatus ===
        OPERATIONAL_SCOPE_STATUS.ACCESS_DISABLED
    ? "Operational access for this account is disabled."
    : scopeStatus ===
        OPERATIONAL_SCOPE_STATUS.MISSING_TENANT
    ? "This account does not have a valid tenant scope."
    : scopeStatus ===
        OPERATIONAL_SCOPE_STATUS.MISSING_FACILITY
    ? "This workspace requires a valid facility scope."
    : "";


/*
 * This is the primary listener gate that Planner,
 * Worker Dashboard and Facility Pulse will consume.
 */
const isScopeReady =
  scopeStatus ===
  OPERATIONAL_SCOPE_STATUS.READY;

const canStartDataListeners =
  isScopeReady;

const isOperationallyScoped =
  isScopeReady;


/*
 * Compact diagnostic object for screens, logging
 * and future enterprise support tooling.
 */
const scopeHealth = {
  status: scopeStatus,
  ready: isScopeReady,
  errorCode: scopeErrorCode,
  errorMessage: scopeErrorMessage,
};

   const scopeSource =
  hasInvalidWorkspaceRequest
    ? "invalid_workspace_request"
    : requestedFacilityAllowed
    ? "operational_workspace"
    : hasAccessProfile
    ? "access_profile"
    : "user_profile";

    return {
      /* Authenticated actor */
      uid,
      email,
      role: actorRole,
      actorRole,

      accessProfile,
      hasAccessProfile,
      isAccessEnabled,

      /* Operational workspace */
workspaceRole,
workspace: workspaceRole,

requestedWorkspaceRole,
requestedWorkspaceRoleAllowed,
invalidRequestedWorkspaceRole,

tenantId,
actorTenantId,
requestedTenantId,
requestedTenantAllowed,
invalidRequestedTenant,

facilityId,
      primaryFacilityId,
      allowedFacilityIds,
     requestedFacilityId,
requestedFacilityAllowed,
invalidRequestedFacility,

hasInvalidWorkspaceRequest,
scopeErrorCode,
scopeErrorMessage,
scopeSource,

      /* Actor classification */
      isOwnerAdmin,
      isFinanceAdmin,
      isOperationsAdmin,
      isAgencyAdmin,
      isPlatformAdmin,
      isPlanner: actorIsPlanner,
      isFacilityPulseUser: actorIsFacilityPulseUser,
      isWorker: actorIsWorker,

      /* Workspace classification */
      isPlannerWorkspace,
      isFacilityPulseWorkspace,
      isWorkerWorkspace,
      canOperatePlannerWorkspace,
      canOperateFacilityPulseWorkspace,
      canPreviewWorkerWorkspace,

      /* Capabilities */
      canViewAllFacilities,
      canCreateShifts: hasPermission(
        safeProfile,
        PERMISSIONS.CAN_CREATE_SHIFTS
      ),
      canCompleteShifts: hasPermission(
        safeProfile,
        PERMISSIONS.CAN_COMPLETE_SHIFTS
      ),
      canAssignWorkers: hasPermission(
        safeProfile,
        PERMISSIONS.CAN_ASSIGN_WORKERS
      ),
      canReassignWorkers: hasPermission(
        safeProfile,
        PERMISSIONS.CAN_REASSIGN_WORKERS
      ),
      canCancelShifts: hasPermission(
        safeProfile,
        PERMISSIONS.CAN_CANCEL_SHIFTS
      ),
      canDeleteShifts: hasPermission(
        safeProfile,
        PERMISSIONS.CAN_DELETE_SHIFTS
      ),

    /* Health */
hasTenantScope,
hasFacilityScope,
hasActorRole,

workspaceRequiresFacilityScope,
hasRequiredFacilityScope,

hasInvalidWorkspaceRequest,
scopeStatus,
scopeErrorCode,
scopeErrorMessage,
scopeHealth,

isScopeReady,
canStartDataListeners,
isOperationallyScoped,
    };
  }, [profile, options]);
}
